import { Avatar, Box, IconButton, Stack, Typography, Tooltip } from '@mui/material';
import { useState } from 'react';
import { RecentChannelDto } from 'tdc-web-backend/channels/schemas';
import FloatingChatElement from './FloatingChatElement';
import { ReactComponent as UpArrowIcon } from '../../../assets/icons/chat-icons/caret-up-fill.svg';
import { ReactComponent as DownArrowIcon } from '../../../assets/icons/chat-icons/caret-down-fill.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/chat-icons/x-circle-fill.svg';
import { ReactComponent as RobotIcon } from '../../../assets/icons/chat-icons/robot.svg';
import { ReactComponent as ChatBubbleIcon } from '../../../assets/icons/chat-icons/chat3-fill.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/chat-icons/plus.svg';
import { ReactComponent as XIcon } from '../../../assets/icons/chat-icons/x.svg';
import { primaryDark, secondaryBlue } from '../../../utils/color';
import useGetOne from '../../../utils/hooks/crud-hooks/useGetOne';
import RecentChannel from './RecentChannel';
import ChannelView from '../Channel/ChannelView';
import useAuth from '../../../utils/hooks/useAuth';
import { useAiChatStore, AiConversation } from '../useAiChatStore';
import FloatingAiAssistant from './FloatingAiAssistant';
import { useFloatingChatStore } from '../useFloatingChatStore';
import axios from '../../../api/axios';
import { useEffect } from 'react';

export default () => {
  const {
    authData: { userData },
  } = useAuth();

  const { mode, expanded, visible, setMode, setExpanded } = useFloatingChatStore();
  const [selectedChannel, setSelectedChannel] = useState('');

  const {
    activeConversationId,
    conversations,
    setActiveConversation,
    createConversation,
    deleteConversation,
    createAiConversation,
    setConversations,
  } = useAiChatStore();

  useEffect(() => {
    const loadSessionsAndMessages = async () => {
      if (!userData?.id) return;
      try {
        const sessionsResp = await axios.get('/ai/sessions', {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        });

        const sessions = sessionsResp.data ?? [];

        const sessionsWithMessages = await Promise.all(
          sessions.map(async (s: any) => {
            try {
              const msgsResp = await axios.get(
                `/ai/sessions/${s.id}/messages`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': String(userData.id),
                  },
                },
              );

              const msgs = (msgsResp.data ?? [])
                .map((m: any) => ({
                  id: String(m.id),
                  role: (m.role ?? 'assistant') as 'assistant' | 'user',
                  content: m.content ?? '',
                  timestamp: m.created_at ? new Date(m.created_at) : new Date(),
                }))
                .sort((a: any, b: any) => a.timestamp.getTime() - b.timestamp.getTime());

              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: msgs,
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at
                  ? new Date(s.updated_at)
                  : s.created_at
                  ? new Date(s.created_at)
                  : new Date(),
              } as AiConversation;
            } catch (err) {
              // if message fetch fails, return session without messages
              // eslint-disable-next-line no-console
              console.error(`Failed to load messages for session ${s.id}:`, err);
              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: [],
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at ? new Date(s.updated_at) : new Date(),
              } as AiConversation;
            }
          }),
        );

        setConversations(sessionsWithMessages);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load AI sessions:', err);
      }
    };

    loadSessionsAndMessages();
  }, [userData?.id, setConversations]);

  const recentChannelsResponse = useGetOne<RecentChannelDto[]>({
    resource: `channels/recent`,
  });
  const recentChannels = recentChannelsResponse.data?.data ?? [];

  if (!visible) {
    return null;
  }

  return (
    <>
      <Stack
        position="fixed"
        bottom={0}
        right={30}
        direction="row"
        gap="10px"
        alignItems="flex-end"
        zIndex={999999}
      >
        {selectedChannel ? (
          <FloatingChatElement position="relative" width="410px" height="640px">
            <Box position="absolute" top="8px" right="16px" zIndex={1}>
              <IconButton sx={{ p: 0 }} disableRipple onClick={() => setSelectedChannel('')}>
                <CloseIcon fill={primaryDark[500]} />
              </IconButton>
            </Box>
            <ChannelView channelId={selectedChannel} floating />
          </FloatingChatElement>
        ) : null}
        <Stack width={300} gap={1}>
          <FloatingChatElement sx={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" p="10px">
              <Stack direction="row" alignItems="center" gap="10px">
                <Avatar>{userData?.firstName?.[0]}</Avatar>
                <Typography variant="heading3" color="primaryDark.600">
                  {mode === 'ai' ? 'AI assistant' : 'chat'}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                {mode === 'ai' && (
                  <Tooltip title="New Conversation" placement="top">
                    <IconButton
                      sx={{ p: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        createAiConversation();
                      }}
                    >
                      <PlusIcon fill={secondaryBlue[500]} width={20} height={20} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={mode === 'ai' ? 'Switch to Spaces Chat' : 'Switch to AI Assistant'}
                  placement="top"
                >
                  <IconButton
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMode(mode === 'ai' ? 'spaces' : 'ai');
                      // Keep expanded state when switching modes
                    }}
                  >
                    {mode === 'ai' ? (
                      <ChatBubbleIcon fill={secondaryBlue[500]} width={20} height={20} />
                    ) : (
                      <RobotIcon fill={secondaryBlue[500]} width={20} height={20} />
                    )}
                  </IconButton>
                </Tooltip>
                {expanded ? (
                  <DownArrowIcon fill={secondaryBlue[500]} />
                ) : (
                  <UpArrowIcon fill={primaryDark[500]} />
                )}
              </Stack>
            </Stack>
          </FloatingChatElement>
          {expanded && (
            <FloatingChatElement>
              {mode === 'spaces' ? (
                <Stack>
                  {recentChannels.map((recentChannel) => (
                    <RecentChannel
                      key={recentChannel.channel.id}
                      recentChannel={recentChannel}
                      onClick={() => setSelectedChannel(recentChannel.channel.id)}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack maxHeight="400px" sx={{ overflow: 'hidden' }}>
                  <Stack sx={{ overflowY: 'auto', minHeight: 0 }}>
                    {conversations.length === 0 ? (
                      <Stack py={2} px="10px" alignItems="center">
                        <Typography
                          variant="body3"
                          color="primaryDark.400"
                          textAlign="center"
                          fontSize="11px"
                        >
                          No conversations yet.
                          <br />
                          Start a new one!
                        </Typography>
                      </Stack>
                    ) : (
                      conversations.slice(0, 5).map((conversation) => {
                        const lastMessage = conversation.messages[conversation.messages.length - 1];
                        const timestamp =
                          lastMessage?.timestamp || conversation.createdAt || new Date();

                        return (
                          <Stack
                            key={conversation.id}
                            direction="row"
                            p="10px"
                            gap="10px"
                            onClick={() => {
                              setActiveConversation(conversation.id);
                            }}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor:
                                activeConversationId === conversation.id
                                  ? 'secondaryBlue.100'
                                  : 'transparent',
                              '&:hover': {
                                backgroundColor: 'secondaryBlue.50',
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: 'secondaryBlue.500',
                              }}
                            >
                              <RobotIcon width={20} height={20} fill="#fff" />
                            </Avatar>
                            <Stack flex="1">
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Typography variant="bodyMedium3" color="primaryDark.600">
                                  {conversation.title}
                                </Typography>
                                <Typography variant="bodySemiBold4" color="primaryDark.400">
                                  {(() => {
                                    const now = new Date().getTime();
                                    const then = new Date(timestamp).getTime();
                                    const diff = now - then;
                                    const minutes = Math.floor(diff / 60000);
                                    const hours = Math.floor(diff / 3600000);
                                    const days = Math.floor(diff / 86400000);

                                    if (minutes < 1) return 'just now';
                                    if (minutes < 60) return `${minutes}m ago`;
                                    if (hours < 24) return `${hours}h ago`;
                                    return `${days}d ago`;
                                  })()}
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography
                                  variant="body4"
                                  fontSize={11}
                                  color="primaryDark.500"
                                  noWrap
                                  sx={{ flex: 1, mr: 1 }}
                                >
                                  {lastMessage?.content || 'No messages yet'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteConversation(conversation.id);
                                  }}
                                  sx={{
                                    color: 'primaryDark.400',
                                    padding: 0.25,
                                    opacity: 0.6,
                                    '&:hover': {
                                      backgroundColor: 'primaryDark.100',
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <XIcon width={12} height={12} />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Stack>
                        );
                      })
                    )}
                  </Stack>
                </Stack>
              )}
            </FloatingChatElement>
          )}
        </Stack>
      </Stack>
      <Stack
        position="fixed"
        bottom={0}
        right={340}
        direction="row"
        alignItems="flex-end"
        zIndex={999999}
      >
        {mode === 'ai' && activeConversationId && (
          <FloatingAiAssistant onClose={() => setActiveConversation(null)} />
        )}
      </Stack>
    </>
  );
};
